<template>
  <div v-if="visible" class="force-change-pwd-mask">
    <div class="force-change-pwd-panel">
      <div class="force-change-pwd-header">{{ dialogTitle }}</div>
      <p class="force-change-pwd-tip">{{ tipText }}</p>
      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-width="100px"
        class="force-change-pwd-form"
        @submit.native.prevent
      >
        <el-form-item label="原密码" prop="oldPassword">
          <el-input
            v-model="form.oldPassword"
            type="password"
            show-password
            placeholder="请输入原密码"
            autocomplete="current-password"
          />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="form.newPassword"
            type="password"
            show-password
            placeholder="请输入新密码"
            autocomplete="new-password"
          />
        </el-form-item>
        <el-form-item label="确认新密码" prop="newPassword1">
          <el-input
            v-model="form.newPassword1"
            type="password"
            show-password
            placeholder="请再次输入新密码"
            autocomplete="new-password"
          />
        </el-form-item>
        <p class="force-change-pwd-rule">{{ passwordRuleTip }}</p>
        <el-form-item class="force-change-pwd-actions">
          <el-button type="primary" :loading="submitting" @click="handleSubmit">确定修改</el-button>
          <el-button :loading="loggingOut" @click="handleLogout">退出登录</el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import md5 from 'js-md5'
import { updatePassword } from '@/api/system/user'
import {
  PASSWORD_RULE_TIP,
  createNewPasswordValidator
} from '@/utils/password'

export default {
  name: 'ForceChangePwdDialog',
  data() {
    const equalToPassword = (rule, value, callback) => {
      if (this.form.newPassword !== value) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    return {
      submitting: false,
      loggingOut: false,
      passwordRuleTip: PASSWORD_RULE_TIP,
      form: {
        oldPassword: '',
        newPassword: '',
        newPassword1: ''
      },
      rules: {
        oldPassword: [
          { required: true, message: '原密码不能为空', trigger: 'blur' }
        ],
        newPassword: [
          {
            required: true,
            validator: createNewPasswordValidator(() => this.form.oldPassword),
            trigger: 'blur'
          }
        ],
        newPassword1: [
          { required: true, message: '确认密码不能为空', trigger: 'blur' },
          { validator: equalToPassword, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    visible() {
      return !!this.$store.getters.token && !!this.$store.getters.needChangePwd
    },
    dialogTitle() {
      if (Number(this.$store.getters.forceChangePassword) === 1) {
        return '首次登录 / 重置密码后请修改密码'
      }
      if (this.$store.getters.passwordExpired) {
        return '密码已过期，请修改密码'
      }
      return '请修改密码'
    },
    tipText() {
      if (this.$store.getters.passwordExpired) {
        return '您的密码已超过 90 天未更新，须修改后才能继续使用系统。'
      }
      return '为保障账号安全，须修改密码后才能进入系统。'
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          document.body.classList.add('force-pwd-open')
          document.body.style.overflow = 'hidden'
        } else {
          document.body.classList.remove('force-pwd-open')
          document.body.style.overflow = ''
          this.resetForm()
        }
      }
    }
  },
  beforeDestroy() {
    document.body.classList.remove('force-pwd-open')
    document.body.style.overflow = ''
  },
  methods: {
    resetForm() {
      this.form = {
        oldPassword: '',
        newPassword: '',
        newPassword1: ''
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    clearRememberPassword() {
      Cookies.remove('password')
      Cookies.remove('rememberMe')
    },
    goLogin() {
      this.clearRememberPassword()
      this.$router.replace('/login').catch(() => {})
      setTimeout(() => {
        if (this.$route.path !== '/login') {
          location.href = '/login'
        }
      }, 300)
    },
    handleLogout() {
      this.loggingOut = true
      this.$store.dispatch('LogOut').then(() => {
        this.goLogin()
      }).catch(() => {
        this.$store.dispatch('FedLogOut').then(() => {
          this.goLogin()
        })
      }).finally(() => {
        this.loggingOut = false
      })
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return
        }
        this.submitting = true
        updatePassword(
          md5(this.form.oldPassword),
          md5(this.form.newPassword),
          md5(this.form.newPassword1)
        ).then(res => {
          if (res.code === 200) {
            this.$message.success('修改成功，请重新登录')
            this.$store.dispatch('LogOut').then(() => {
              this.goLogin()
            }).catch(() => {
              this.$store.dispatch('FedLogOut').then(() => {
                this.goLogin()
              })
            })
          }
        }).finally(() => {
          this.submitting = false
        })
      })
    }
  }
}
</script>

<style lang="scss">
/* 自建全屏层，避免 el-dialog 与 .v-modal 层级错位导致无法点击 */
.force-change-pwd-mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.55);
  box-sizing: border-box;
}

.force-change-pwd-panel {
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 48px);
  overflow: auto;
  padding: 24px 28px 16px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  box-sizing: border-box;
}

.force-change-pwd-header {
  margin-bottom: 12px;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
}

.force-change-pwd-tip {
  margin: 0 0 20px;
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
}

.force-change-pwd-form {
  width: 100%;
}

.force-change-pwd-rule {
  margin: 0 0 16px 100px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.force-change-pwd-actions {
  margin-bottom: 8px;

  .el-form-item__content {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
